import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  List, 
  ListItem, 
  ListItemAvatar, 
  Avatar, 
  ListItemText, 
  Divider, 
  Button, 
  TextField, 
  Chip, 
  IconButton, 
  Skeleton, 
  Alert, 
  Tooltip, 
  Badge, 
  useMediaQuery,
  Stack,
  Paper,
  Collapse
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Close,
  PersonAdd,
  Refresh,
  ContentCopy,
  Email,
  Person,
  Send,
  Group,
  PersonRemove,
  Shield,
  Star,
  SwapHoriz,
  Check,
  Cancel
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';
import API from '../../API';

/* ------------------------------------------------------------------ */
/* Helper Functions                                                  */
/* ------------------------------------------------------------------ */
function formatLastOnline(lastOnline) {
  if (!lastOnline) return 'Never logged in';
  
  const now = new Date();
  const dt = new Date(lastOnline);
  const diffMs = now - dt;
  
  if (diffMs < 0) return 'Unknown';
  
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Online now';
  if (diffMins < 5) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  
  return dt.toLocaleDateString();
}

/* ------------------------------------------------------------------ */
/* Enhanced ParticipantsPanel - Icon-Based Controls                  */
/* ------------------------------------------------------------------ */
export default function ParticipantsPanel({ conversationId, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  
  // State management
  const [inviteEmails, setInviteEmails] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Inline remove confirmation
  const [removingUserId, setRemovingUserId] = useState(null);
  
  // NEW: Inline transfer host confirmation
  const [transferringToUserId, setTransferringToUserId] = useState(null);

  /* ------------------------------------------------------------------ */
  /* User Search with Debounce                                         */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!userSearch || userSearch.length < 2) {
      setSearchResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setSearching(true);
      
      try {
        const response = await API.globalUsers.search({
          q: userSearch,
          limit: 10
        });
        
        const users = response?.users || [];
        setSearchResults(users);
      } catch (error) {
        console.error('[SEARCH-API] Error:', error);
        setSearchResults([]);
        toast.error('Failed to search users');
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [userSearch]);

  /* ------------------------------------------------------------------ */
  /* Data Fetching                                                    */
  /* ------------------------------------------------------------------ */
  const {
    data: metadata,
    isLoading: metadataLoading,
    error: metadataError,
    refetch: refetchMetadata
  } = useQuery(
    ['conversation', 'metadata', conversationId],
    () => API.conversations.getMetadata(conversationId),
    { 
      enabled: !!conversationId,
      staleTime: 1000 * 60 * 5, 
    }
  );

  const {
    data: conversationData,
    isLoading: messagesLoading,
  } = useQuery(
    ['messages', conversationId],
    () => API.conversations.getMessages(conversationId),
    { 
      enabled: !!conversationId,
      staleTime: 1000 * 60 * 2, 
    }
  );

  /* ------------------------------------------------------------------ */
  /* Use backend's authoritative host detection                        */
  /* ------------------------------------------------------------------ */
  const isCurrentUserHost = useMemo(() => {
    const isHost = metadata?.conversation?.is_current_user_host || false;
    console.log('[ParticipantsPanel] Backend says is_current_user_host:', isHost);
    return isHost;
  }, [metadata]);

  const currentUserGlobalId = useMemo(() => {
    const globalId = metadata?.conversation?.current_user_global_id;
    console.log('[ParticipantsPanel] Backend says current_user_global_id:', globalId);
    return globalId;
  }, [metadata]);

  const hostGlobalUserId = useMemo(() => {
    return metadata?.conversation?.host_global_user_id;
  }, [metadata]);

  const hostInfo = useMemo(() => {
    return metadata?.conversation?.host;
  }, [metadata]);

  /* ------------------------------------------------------------------ */
  /* Process all participants with proper deduplication                */
  /* ------------------------------------------------------------------ */
  const allParticipants = useMemo(() => {
    const regularParticipants = conversationData?.participants || [];
    const globalParticipants = metadata?.conversation?.global_participants || [];
    
    const participantMap = new Map();
    
    regularParticipants.forEach(p => {
      const key = String(p.global_user_id || p.id);
      participantMap.set(key, {
        ...p,
        isGlobalParticipant: false,
        isRegularParticipant: true,
        global_user_id: p.global_user_id || p.id,
      });
    });
    
    globalParticipants.forEach(gp => {
      const key = String(gp.id);
      const existing = participantMap.get(key);
      
      if (existing) {
        participantMap.set(key, {
          ...existing,
          ...gp,
          isGlobalParticipant: true,
          global_user_id: gp.id,
        });
      } else {
        participantMap.set(key, {
          ...gp,
          id: gp.id,
          username: gp.username,
          pfp: gp.avatar || gp.pfp,
          is_creator: gp.user_type === 'creator',
          isGlobalParticipant: true,
          isRegularParticipant: false,
          global_user_id: gp.id,
        });
      }
    });
    
    console.log('[ParticipantsPanel] Deduplicated participants:', participantMap.size, 'unique participants');
    return Array.from(participantMap.values());
  }, [conversationData?.participants, metadata?.conversation?.global_participants]);

  const hostParticipant = useMemo(() => {
    if (!hostGlobalUserId) return null;
    return allParticipants.find(p => Number(p.global_user_id) === Number(hostGlobalUserId));
  }, [allParticipants, hostGlobalUserId]);

  const otherCreators = useMemo(() => {
    return allParticipants.filter(p => 
      p.is_creator && 
      Number(p.global_user_id) !== Number(hostGlobalUserId)
    );
  }, [allParticipants, hostGlobalUserId]);

  const brandUsers = useMemo(() => {
    return allParticipants.filter(p => 
      !p.is_creator && 
      Number(p.global_user_id) !== Number(hostGlobalUserId)
    );
  }, [allParticipants, hostGlobalUserId]);

  /* ------------------------------------------------------------------ */
  /* Mutations                                                        */
  /* ------------------------------------------------------------------ */
  const inviteByEmailMutation = useMutation(
    (payload) => API.conversations.inviteByEmail(conversationId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['conversation', 'metadata', conversationId]);
        queryClient.invalidateQueries(['messages', conversationId]);
        setInviteEmails('');
        toast.success('Email invitations sent!');
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to send invitations';
        toast.error('Failed to send invitations', { description: errorMessage });
      },
    }
  );

  const addParticipantMutation = useMutation(
    (data) => API.conversations.addParticipant(conversationId, data),
    {
      onSuccess: (response) => {
        const addedUsername = selectedUser?.username || selectedUser?.name || selectedUser?.email;
        toast.success('Participant added!', {
          description: `${addedUsername} has been added to the conversation.`,
        });
        
        queryClient.invalidateQueries(['conversation', 'metadata', conversationId]);
        queryClient.invalidateQueries(['messages', conversationId]);
        
        setSelectedUser(null);
        setUserSearch('');
        setSearchResults([]);
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to add participant';
        
        if (errorMessage.includes('already a participant')) {
          toast.error('Already a member', { 
            description: 'This user is already in the conversation.' 
          });
        } else if (errorMessage.includes('Not authorized')) {
          toast.error('Permission denied', { 
            description: 'You don\'t have permission to add participants.' 
          });
        } else {
          toast.error('Failed to add participant', { description: errorMessage });
        }
      },
    }
  );

  const removeParticipantMutation = useMutation(
    (data) => API.conversations.removeParticipant(conversationId, data),
    {
      onSuccess: (response, variables) => {
        const removedParticipant = allParticipants.find(p => p.global_user_id === variables.global_user_id);
        const removedUsername = removedParticipant?.username || 'User';
        
        toast.success('Participant removed', {
          description: `${removedUsername} has been removed from the conversation.`,
        });
        
        queryClient.invalidateQueries(['conversation', 'metadata', conversationId]);
        queryClient.invalidateQueries(['messages', conversationId]);
        
        setRemovingUserId(null);
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to remove participant';
        
        if (errorMessage.includes('Cannot remove')) {
          toast.error('Cannot remove', { description: errorMessage });
        } else if (errorMessage.includes('Only')) {
          toast.error('Permission denied', { description: errorMessage });
        } else {
          toast.error('Failed to remove participant', { description: errorMessage });
        }
        
        setRemovingUserId(null);
      },
    }
  );

  const transferHostMutation = useMutation(
    (data) => API.conversations.transferHost(conversationId, data),
    {
      onSuccess: (response, variables) => {
        const newHost = allParticipants.find(p => Number(p.global_user_id) === Number(variables.new_host_global_user_id));
        const newHostName = newHost?.username || 'New Host';
        
        toast.success('Host transferred!', {
          description: `${newHostName} is now the conversation host.`,
        });
        
        queryClient.invalidateQueries(['conversation', 'metadata', conversationId]);
        queryClient.invalidateQueries(['messages', conversationId]);
        
        setTransferringToUserId(null);
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to transfer host';
        toast.error('Failed to transfer host', { description: errorMessage });
        setTransferringToUserId(null);
      },
    }
  );

  /* ------------------------------------------------------------------ */
  /* Handlers                                                         */
  /* ------------------------------------------------------------------ */
  const handleInviteClick = useCallback(() => {
    const trimmed = inviteEmails.trim();
    if (!trimmed) return;
    const emailsArray = trimmed.split(',').map(email => email.trim()).filter(Boolean);
    inviteByEmailMutation.mutate({ emails: emailsArray });
  }, [inviteEmails, inviteByEmailMutation]);

  const handleShare = useCallback(() => {
    const url = `${window.location.origin}/join/${conversationId}`;
    if (navigator.share) {
      navigator.share({ title: 'Join Conversation', url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  }, [conversationId]);

  const handleAddUser = useCallback(() => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }
    
    addParticipantMutation.mutate({ global_user_id: selectedUser.id });
  }, [selectedUser, addParticipantMutation]);

  const handleUserSelect = useCallback((user) => {
    setSelectedUser(user);
    setUserSearch('');
    setSearchResults([]);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedUser(null);
  }, []);

  const handleClearSearch = useCallback(() => {
    setUserSearch('');
    setSearchResults([]);
  }, []);

  // Inline remove handlers
  const handleRemoveClick = useCallback((participant) => {
    setRemovingUserId(participant.global_user_id);
  }, []);

  const handleRemoveConfirm = useCallback((participant) => {
    removeParticipantMutation.mutate({ 
      global_user_id: participant.global_user_id || participant.id 
    });
  }, [removeParticipantMutation]);

  const handleRemoveCancel = useCallback(() => {
    setRemovingUserId(null);
  }, []);

  // NEW: Inline transfer host handlers
  const handleMakeHostClick = useCallback((participant) => {
    setTransferringToUserId(participant.global_user_id);
  }, []);

  const handleTransferHostConfirm = useCallback((participant) => {
    transferHostMutation.mutate({ 
      new_host_global_user_id: Number(participant.global_user_id)
    });
  }, [transferHostMutation]);

  const handleTransferHostCancel = useCallback(() => {
    setTransferringToUserId(null);
  }, []);

  /* ------------------------------------------------------------------ */
  /* Render Participant with Inline Actions                           */
  /* ------------------------------------------------------------------ */
  const renderParticipant = (participant, role = 'regular') => {
    const isOnline = participant.last_online && new Date() - new Date(participant.last_online) < 300000;
    const safeUsername = String(participant.username || '?');
    const isHost = Number(participant.global_user_id) === Number(hostGlobalUserId);
    const isCurrentUser = Number(participant.global_user_id) === Number(currentUserGlobalId);
    const canRemove = isCurrentUserHost && !isHost && !isCurrentUser;
    const canMakeHost = isCurrentUserHost && !isHost && !isCurrentUser;
    const isRemoving = removingUserId === participant.global_user_id;
    const isTransferring = transferringToUserId === participant.global_user_id;
    
    return (
      <Box key={participant.global_user_id || participant.id}>
        <ListItem
          sx={{
            py: 1.5,
            px: 3,
            transition: 'background-color 0.2s',
            '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.5) },
            bgcolor: isRemoving 
              ? alpha(theme.palette.error.main, 0.05) 
              : isTransferring 
                ? alpha(theme.palette.warning.main, 0.05) 
                : 'transparent',
          }}
          secondaryAction={
            !isRemoving && !isTransferring ? (
              <Stack direction="row" spacing={0.5}>
                {/* Make Host Button */}
                {canMakeHost && (
                  <Tooltip title="Make host">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleMakeHostClick(participant)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'warning.main',
                          bgcolor: alpha(theme.palette.warning.main, 0.08),
                        },
                      }}
                    >
                      <SwapHoriz fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                
                {/* Remove Button */}
                {canRemove && (
                  <Tooltip title="Remove from conversation">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemoveClick(participant)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'error.main',
                          bgcolor: alpha(theme.palette.error.main, 0.08),
                        },
                      }}
                    >
                      <PersonRemove fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            ) : null
          }
        >
          <ListItemAvatar>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              variant="dot"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: isOnline ? theme.palette.success.main : theme.palette.grey[400],
                  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                }
              }}
            >
              <Avatar 
                src={participant.pfp || participant.avatar || ''} 
                sx={{ 
                  width: 40, 
                  height: 40,
                  border: isHost ? `2px solid ${alpha(theme.palette.warning.main, 0.5)}` : 'none',
                }}
              >
                {safeUsername.charAt(0).toUpperCase()}
              </Avatar>
            </Badge>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight="600" color="text.primary">
                  {participant.username || 'Unknown User'}
                </Typography>
                {isHost && (
                  <>
                    <Chip 
                      icon={<Shield sx={{ fontSize: '0.75rem' }} />}
                      label="HOST" 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                    />
                    {isCurrentUser && (
                      <Chip 
                        label="YOU" 
                        size="small" 
                        color="primary" 
                        variant="filled"
                        sx={{ 
                          height: 18, 
                          fontSize: '0.65rem', 
                          fontWeight: 700,
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        }}
                      />
                    )}
                  </>
                )}
                {role === 'creator' && !isHost && (
                  <Chip 
                    icon={<Star sx={{ fontSize: '0.75rem' }} />}
                    label="CREATOR" 
                    size="small" 
                    color="info" 
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                  />
                )}
                {!isHost && isCurrentUser && (
                  <Chip 
                    label="YOU" 
                    size="small" 
                    color="default" 
                    variant="outlined"
                    sx={{ 
                      height: 18, 
                      fontSize: '0.65rem', 
                      fontWeight: 700,
                    }}
                  />
                )}
              </Box>
            }
            secondary={
              <Typography variant="caption" color="text.secondary">
                {formatLastOnline(participant.last_online || participant.last_online_date)}
              </Typography>
            }
          />
        </ListItem>

        {/* Inline remove confirmation */}
        <Collapse in={isRemoving}>
          <Box 
            sx={{ 
              px: 3, 
              pb: 2, 
              bgcolor: alpha(theme.palette.error.main, 0.05),
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Alert 
              severity="warning" 
              sx={{ mb: 1.5 }}
              icon={false}
            >
              <Typography variant="caption" fontWeight="600">
                Remove <strong>{participant.username}</strong> from this conversation?
              </Typography>
            </Alert>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                onClick={handleRemoveCancel}
                startIcon={<Cancel fontSize="small" />}
                sx={{ fontSize: '0.75rem' }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => handleRemoveConfirm(participant)}
                disabled={removeParticipantMutation.isLoading}
                startIcon={
                  removeParticipantMutation.isLoading ? 
                    <CircularProgress size={14} color="inherit" /> : 
                    <Check fontSize="small" />
                }
                sx={{ fontSize: '0.75rem' }}
              >
                {removeParticipantMutation.isLoading ? 'Removing...' : 'Remove'}
              </Button>
            </Stack>
          </Box>
        </Collapse>

        {/* NEW: Inline transfer host confirmation */}
        <Collapse in={isTransferring}>
          <Box 
            sx={{ 
              px: 3, 
              pb: 2, 
              bgcolor: alpha(theme.palette.warning.main, 0.05),
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Alert 
              severity="warning" 
              sx={{ mb: 1.5 }}
              icon={false}
            >
              <Typography variant="caption" fontWeight="600" display="block" sx={{ mb: 0.5 }}>
                Make <strong>{participant.username}</strong> the host?
              </Typography>
              <Typography variant="caption" color="text.secondary">
                You will no longer be the host and they will have full control over this conversation.
              </Typography>
            </Alert>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                onClick={handleTransferHostCancel}
                startIcon={<Cancel fontSize="small" />}
                sx={{ fontSize: '0.75rem' }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={() => handleTransferHostConfirm(participant)}
                disabled={transferHostMutation.isLoading}
                startIcon={
                  transferHostMutation.isLoading ? 
                    <CircularProgress size={14} color="inherit" /> : 
                    <SwapHoriz fontSize="small" />
                }
                sx={{ fontSize: '0.75rem' }}
              >
                {transferHostMutation.isLoading ? 'Transferring...' : 'Make Host'}
              </Button>
            </Stack>
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        boxSizing: 'border-box',
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          py: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(12px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Group color="action" />
          <Box>
            <Typography variant="subtitle1" fontWeight="700" lineHeight={1.2}>
              Participants
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {allParticipants.length} {allParticipants.length === 1 ? 'member' : 'members'}
              {isCurrentUserHost && ' • You\'re the host'}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={refetchMetadata} size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton onClick={onClose} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Scrollable Content */}
      <Box 
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { 
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', 
            borderRadius: '4px' 
          },
        }}
      >
        {metadataLoading && (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="60%" />
                  <Skeleton width="40%" />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {metadataError && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" variant="outlined">Could not load participants.</Alert>
          </Box>
        )}

        {!metadataLoading && (
          <>
            {/* Add Participant Section */}
            <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAdd fontSize="small" />
                Add Participant
              </Typography>

              {/* Search Input */}
              <Box sx={{ position: 'relative', mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Search by username or email"
                  variant="outlined"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  disabled={!!selectedUser}
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {searching && <CircularProgress color="inherit" size={20} />}
                        {userSearch && !selectedUser && (
                          <IconButton size="small" onClick={handleClearSearch}>
                            <Close fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ),
                  }}
                />
                
                {/* Search Results Dropdown */}
                {userSearch.length >= 2 && !selectedUser && searchResults.length > 0 && (
                  <Paper
                    elevation={8}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      mt: 1,
                      maxHeight: 300,
                      overflowY: 'auto',
                      zIndex: 1000,
                      borderRadius: 2,
                    }}
                  >
                    <List disablePadding>
                      {searchResults.map((user) => (
                        <ListItem
                          key={user.id}
                          button
                          onClick={() => handleUserSelect(user)}
                          sx={{
                            gap: 1.5,
                            py: 1.5,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                              <Person fontSize="small" />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {user.username || user.email}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                {user.name && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {user.name}
                                  </Typography>
                                )}
                                <Chip
                                  label={user.user_type === 'creator' ? 'Creator' : 'User'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
                
                {/* No Results Message */}
                {userSearch.length >= 2 && !searching && searchResults.length === 0 && !selectedUser && (
                  <Paper
                    elevation={8}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      mt: 1,
                      p: 2,
                      zIndex: 1000,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No users found
                    </Typography>
                  </Paper>
                )}
              </Box>

              {/* Selected User Preview */}
              {selectedUser && (
                <Paper 
                  sx={{ 
                    p: 2, 
                    mb: 2, 
                    borderRadius: 2, 
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
                      <Person />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedUser.username || selectedUser.email}
                      </Typography>
                      {selectedUser.name && (
                        <Typography variant="body2" color="text.secondary">
                          {selectedUser.name}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={selectedUser.user_type === 'creator' ? 'Creator' : 'User'}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600 }}
                    />
                    <IconButton size="small" onClick={handleClearSelection}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              )}

              {/* Add Button */}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={handleAddUser}
                disabled={!selectedUser || addParticipantMutation.isLoading}
                startIcon={addParticipantMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                sx={{ height: 48, borderRadius: 2, fontWeight: 600 }}
              >
                {addParticipantMutation.isLoading ? 'Adding...' : 'Add to Conversation'}
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Current Participants List */}
            {allParticipants.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight="700" sx={{ px: 3, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Group fontSize="small" />
                  Current Members
                </Typography>
                <List disablePadding>
                  {hostParticipant && renderParticipant(hostParticipant, hostParticipant.is_creator ? 'creator' : 'regular')}
                  {otherCreators.map(p => renderParticipant(p, 'creator'))}
                  {brandUsers.map(p => renderParticipant(p, 'regular'))}
                </List>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Sticky Footer */}
      <Box
        sx={{
          p: 3,
          bgcolor: 'background.paper',
          borderTop: `1px solid ${theme.palette.divider}`,
          boxShadow: '0px -4px 20px rgba(0,0,0,0.05)',
        }}
      >
        <Stack spacing={2}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleShare}
            startIcon={<ContentCopy />}
            sx={{ justifyContent: 'flex-start', borderRadius: 2 }}
          >
            Copy Invite Link
          </Button>

          <Box>
            <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Or invite via email
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="email@example.com"
                variant="outlined"
                fullWidth
                size="small"
                value={inviteEmails}
                onChange={(e) => setInviteEmails(e.target.value)}
                InputProps={{
                  startAdornment: <Email fontSize="small" color="action" sx={{ mr: 1 }} />,
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Button
                variant="contained"
                onClick={handleInviteClick}
                disabled={inviteByEmailMutation.isLoading || !inviteEmails.trim()}
                sx={{ minWidth: 48, px: 2, borderRadius: 2 }}
              >
                {inviteByEmailMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />}
              </Button>
            </Box>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}